/**
 * Theme access for the donor app.
 *
 * Re-exports `@mfa/theme` unchanged — the tokens are shared with the admin
 * portal and must not fork — and adds the React Native helpers that make them
 * convenient to use in `StyleSheet.create`.
 *
 * Rule for this app: a component may import tokens from here, and may not
 * write a colour, radius, shadow, font size or spacing literal of its own.
 */

import { StyleSheet } from 'react-native';
import {
  colors,
  elevation,
  radius,
  sizing,
  space,
  textStyles,
  theme,
  type ElevationToken,
  type TextRole,
} from '@mfa/theme';

export * from '@mfa/theme';

/**
 * Shadow props for a level, spread into a style object.
 *
 * React Native needs `shadow*` on iOS and `elevation` on Android; the web
 * build maps them to `boxShadow`. Callers should not know that.
 */
export function shadow(level: ElevationToken) {
  const { shadowColor, shadowOffset, shadowOpacity, shadowRadius, elevation: android } =
    elevation[level];
  return { shadowColor, shadowOffset, shadowOpacity, shadowRadius, elevation: android };
}

/** Text role as a style object, for composing into StyleSheet entries. */
export const text = (role: TextRole) => textStyles[role];

/** Styles every screen shares, so cards and rows look identical everywhere. */
export const common = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: colors.bg.page,
  },
  card: {
    backgroundColor: colors.bg.surface,
    borderRadius: radius.lg,
    padding: space.lg,
    ...shadow('card'),
  },
  cardTight: {
    backgroundColor: colors.bg.surface,
    borderRadius: radius.lg,
    overflow: 'hidden',
    ...shadow('card'),
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  rowBetween: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  divider: {
    height: StyleSheet.hairlineWidth,
    backgroundColor: colors.border.subtle,
  },
  /** Horizontal padding used by every screen body. */
  gutter: {
    paddingHorizontal: space.lg,
  },
  /** Minimum tappable area, applied to every pressable. */
  touchTarget: {
    minHeight: sizing.touchTarget,
    minWidth: sizing.touchTarget,
    justifyContent: 'center',
  },
});

export { theme };
